// import "@styles/Debugging.css"
import { ReactComponent as DayPickerSVG } from '@assets/SVGs/PresenteeismTable/DayPickerSVG.svg';
import { DatePickerPopper } from '@components/General/TablePoppers';
import { Application } from '@core/Application';
import { APIIntegration, apiConfig } from '@integrations/APIIntegration';
import { BarChart, ChartsText } from "@mui/x-charts";
import styles from '@styles/screens/ResumenControlBoard.module.css';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';


const xLabels = [
    "00 hs", "01 hs", "02 hs", "03 hs", "04 hs", "05 hs", "06 hs", 
    "07 hs", "08 hs", "09 hs", "10 hs", "11 hs", "12 hs", "13 hs", 
    "14 hs", "15 hs", "16 hs", "17 hs", "18 hs", "19 hs", "20 hs", 
    "21 hs", "22 hs", "23 hs", "24 hs"
];


const CustomAxisTickLabel = (props) => {

    const isNotHour = !props.text.includes('h');

    const textStyle = {
        fill: isNotHour ? '#C5C5C5' : '#9F9F9F',
    };

    const polishedText = props.text;

    // Combine the existing style with the additional styles
    const combinedStyle = {
        ...props.style,
        fill: textStyle.fill,
        textAlign: 'center',
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal'
    };

    return (
        <ChartsText {...props} style={combinedStyle} text={polishedText} />
    );
}


const ResumenControlBoard = ({org}) => {

    const [orgTimezone, setOrgTimezone] = useState(org.timezone ? org.timezone : 'America/Argentina/Buenos_Aires');
    
    const [count, setCount] = useState(0)
    const [validCheckIns, setValidCheckIns] = useState(0)
    const [validCheckOuts, setValidCheckOuts] = useState(0)
    const [entradasPorTiempo, setEntradasPorTiempo] = useState(new Array(24).fill(0)) //posicion 0 del array es la cantidad de entradas en el horario 0:00-0:59
    const [salidasPorTiempo, setSalidasPorTiempo] = useState(new Array(24).fill(0)) //posicion 0 del array es la cantidad de salidas en el horario 0:00-0:59

    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [date, setDate] = useState(new Date())
    const [dayString, setDayString] = useState(new Date().toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        timeZone: orgTimezone
    }).replace(/^(.*\sde\s)(.)(.*)$/, (match, p1, p2, p3) => p1 + p2.toUpperCase() + p3))

    useEffect(() => {
        if (date) {
            setDayString(date.toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                timeZone: orgTimezone
            }).replace(/^(.*\sde\s)(.)(.*)$/, (match, p1, p2, p3) => p1 + p2.toUpperCase() + p3))
    
            fetchStatsResponse()
        } else {
            setDayString("Elegir Fecha")
            setValidCheckIns(0)
            setValidCheckOuts(0)
            setEntradasPorTiempo(new Array(24).fill(0))
            setSalidasPorTiempo(new Array(24).fill(0))
        }


    }, [date])

    useEffect(() => {
        setDayString(date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            timeZone: orgTimezone
        }).replace(/^(.*\sde\s)(.)(.*)$/, (match, p1, p2, p3) => p1 + p2.toUpperCase() + p3))
    }, [orgTimezone])
    

    useEffect(() => {
        if (org.timezone) {
            setOrgTimezone(org.timezone)
        }
        resetState()
        fetchCountResponse()
        fetchStatsResponse()
    }, [org])


    const handleClickDayPicker = (event) => {
        setAnchorEl(event.currentTarget);
        setOpen((previousOpen) => !previousOpen);
    };

    const updateDate = (updatedDate) => {
        setDate(updatedDate)
    }

    const resetState = () => {
        setCount(0)
        setValidCheckIns(0)
        setValidCheckOuts(0)
        setEntradasPorTiempo(new Array(24).fill(0))
        setSalidasPorTiempo(new Array(24).fill(0))
    }

    const fetchCountResponse = async () => {
        try {
            const dashboardService = Application.shared().coreDependencies.dashboardService;
            const result = await dashboardService.getUserCount(org.id)
            setCount(result)
        } catch (error) {
            console.error(error)
        }
    }

    const fetchStatsResponse = async () => {
        try {
            let processedDate = date.toLocaleDateString('en-ZA', {timezone: orgTimezone}).replace(/\//g, '-')
            const dashboardService = Application.shared().coreDependencies.dashboardService;
            const result = await dashboardService.getPresenteeismTableDay(org.id, processedDate)
            setStats(result)
        } catch (error) {
            console.error(error)
        }
    }

    function checkForRecordsTag(data) {
        const hasRecords = Object.entries(data.buildings).some(
            ([, { user_records }]) => {
                return Object.values(user_records).some(user => {
                    if (user.records) {
                        console.log('Records tag found in', user.name);
                        return true;
                    }
                    return false;
                });
            }
        );
        return hasRecords; // Return the result of the 'hasRecords' check.
    }

    const setStats = (data) => {
        let checkIns = 0;
        let checkOuts = 0;
    
        const buildings = data.buildings;

        let updatedEntradas = new Array(24).fill(0)
        let updatedSalidas = new Array(24).fill(0)

        if (!checkForRecordsTag(data)) {
            Object.values(buildings).forEach((building) => {
                const userRecords = building.user_records;
                if (!userRecords) return;
    
                Object.values(userRecords).forEach((record) => {
                    if (record['CHECK-IN'] !== 'Pendiente') {
                        checkIns++;
    
                        let hora = parseInt(record['CHECK-IN'].split(':')[0]);
                        updatedEntradas[hora] += 1
                    }
                    if (record['CHECK-OUT'] !== 'Pendiente') {
                        checkOuts++;
                        let hora = parseInt(record['CHECK-OUT'].split(':')[0]);
    
                        updatedSalidas[hora] += 1
                    }
                });
            });                
        } else {
            Object.values(buildings).forEach((building) => {
                const userRecords = building.user_records;
                if (!userRecords) return;

                Object.values(userRecords).forEach((user) => {
                    const records = user.records
                    if (!records) return;

                    Object.values(records).forEach((record) => {
                        if (record.checkInTimestamp !== 0) {
                            checkIns++

                            let hora = parseInt(new Date(record.checkInTimestamp).toLocaleTimeString('es-AR', {hour: '2-digit', minute: '2-digit', hour12: false, timeZone: orgTimezone}).split(':')[0]);
                            updatedEntradas[hora] += 1
                        }

                        if (record.checkOutTimestamp !== 0) {
                            checkOuts++

                            let hora = parseInt(new Date(record.checkOutTimestamp).toLocaleTimeString('es-AR', {hour: '2-digit', minute: '2-digit', hour12: false, timeZone: orgTimezone}).split(':')[0]);
                            updatedSalidas[hora] += 1
                        }
                    })
                })
    
            })
        }


        setEntradasPorTiempo(updatedEntradas)
        setSalidasPorTiempo(updatedSalidas)
    
        setValidCheckIns(checkIns);
        setValidCheckOuts(checkOuts);
    }



    return (
    <div className={styles['content-block']}>
        <div className={styles['header-container']}>
            <div className={styles['title-container']}>
                <h2 className={styles['title']}>
                    Resumen
                </h2>
                <p className={styles['sub-title']}>{dayString}</p>
            </div>
            <div className={styles['filters-container']}>
                <div className={styles['daypicker']} onClick={handleClickDayPicker}>
                    <div className={styles['svg-container']} style={{marginRight: '8px'}}>
                        <DayPickerSVG/>
                    </div>
                    <p className={styles['item-text']}>Elegir Fecha</p>
                    {open && <DatePickerPopper
                        currentPickedDate={date}
                        updateDate={updateDate} 
                        clickAwayHandler={() => {setOpen(false)}}
                        placement="bottom-end"
                        anchorElement={anchorEl} 
                        isOpen={open} 
                        />
                    }
                </div>
            </div>

        </div>
        <div className={styles['content-container']}>
            <div className={styles["stats-container"]}>
                <div className={`${styles["stat-container"]} ${styles["stat-count"]}`}>

                    <div className={styles["stat-count-icon-container"]}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="32" viewBox="0 0 34 32" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M12.7488 20.0787C18.2214 20.0787 25.4976 20.695 25.4976 26.0265C25.4976 31.6428 17.0193 31.9798 13.3825 32L12.0622 32C8.18451 31.9799 0 31.6448 0 26.0601C0 20.4376 8.4783 20.1002 12.115 20.08L12.552 20.0787C12.6201 20.0787 12.6857 20.0787 12.7488 20.0787ZM12.7488 22.5975C8.08218 22.5975 2.51886 23.197 2.51886 26.0601C2.51886 28.3305 5.9613 29.4824 12.7488 29.4824C19.5363 29.4824 22.9787 28.3187 22.9787 26.0265C22.9787 23.7512 19.5363 22.5975 12.7488 22.5975ZM28.0546 19.2943C32.5986 19.9744 33.5507 22.0785 33.5507 23.7225C33.5507 24.9466 33.0218 26.604 30.4979 27.5595C30.3518 27.6149 30.2006 27.6418 30.0512 27.6418C29.5424 27.6418 29.0638 27.3311 28.874 26.8291C28.6272 26.1792 28.9546 25.4504 29.6045 25.2052C31.0319 24.6645 31.0319 24.0046 31.0319 23.7225C31.0319 22.7703 29.9051 22.1188 27.6835 21.788C26.995 21.6839 26.5197 21.0424 26.6222 20.3556C26.7263 19.6671 27.3795 19.2053 28.0546 19.2943ZM12.7488 0C17.5027 0 21.3718 3.86897 21.3718 8.6229C21.3818 10.9134 20.4934 13.0762 18.8747 14.7051C17.2592 16.3356 15.1014 17.2374 12.8059 17.2458H12.7488C7.99318 17.2458 4.12421 13.3768 4.12421 8.6229C4.12421 3.86897 7.99318 0 12.7488 0ZM24.6506 1.88713C27.9503 2.42785 30.3449 5.25065 30.3449 8.59737C30.3382 11.9643 27.8243 14.8559 24.4961 15.3227C24.4373 15.3311 24.3785 15.3345 24.3198 15.3345C23.7018 15.3345 23.1628 14.8794 23.0738 14.2497C22.9781 13.5612 23.4566 12.9231 24.1451 12.8274C26.2408 12.5335 27.8227 10.7149 27.826 8.5957C27.826 6.48993 26.3181 4.71329 24.2442 4.37409C23.5574 4.2599 23.0922 3.61339 23.2048 2.92658C23.3173 2.23977 23.9671 1.77798 24.6506 1.88713ZM12.7488 2.51886C9.38192 2.51886 6.64307 5.2577 6.64307 8.6229C6.64307 11.9881 9.38192 14.7269 12.7488 14.7269H12.8008C14.4196 14.7202 15.9444 14.0838 17.0879 12.9302C18.2332 11.7799 18.8595 10.2501 18.8529 8.62794C18.8529 5.2577 16.114 2.51886 12.7488 2.51886Z" fill="#EC6C56"/>
                        </svg>
                    </div>
                    <p className={styles["stat-text"]} style={{color: "#32353E", fontSize: "2.5em"}}>
                        {count}
                    </p>
                    <p className={styles["stat-count-text"]}>
                        Cantidad de colaboradores
                    </p>

                </div>
                <div className={styles["stat-container"]}>

                    <div className={styles["stat-header"]} style={{backgroundColor: "#FF9F7D"}}>
                        <p className={styles["stat-header-text"]}>Ingresos</p>
                        <div className={styles["stat-header-icon-container"]}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                                <path d="M7.66667 2.69121H3.66667C2.95942 2.69121 2.28115 2.96273 1.78105 3.44605C1.28095 3.92937 0.999999 4.58489 0.999999 5.26841V6.557M7.66667 23.3088H3.66667C2.95942 23.3088 2.28115 23.0373 1.78105 22.5539C1.28095 22.0706 0.999999 21.4151 0.999999 20.7316V19.443M16.9987 13H17M0.999999 13H7.66667M7.66667 13L5 10.4228M7.66667 13L5 15.5772M23.1 22.5717L15.1 24.8912C14.7018 25.0067 14.2811 25.0307 13.8716 24.9611C13.462 24.8915 13.075 24.7303 12.7415 24.4904C12.4079 24.2505 12.1369 23.9386 11.9503 23.5795C11.7637 23.2204 11.6666 22.8241 11.6667 22.4222V3.57776C11.6666 3.17592 11.7637 2.77962 11.9503 2.42051C12.1369 2.06141 12.4079 1.74946 12.7415 1.50958C13.075 1.26969 13.462 1.10852 13.8716 1.03893C14.2811 0.969348 14.7018 0.993277 15.1 1.10881L23.1 3.42829C23.6493 3.58764 24.1308 3.9139 24.4731 4.35866C24.8153 4.80341 25.0001 5.34296 25 5.89724V20.1028C25.0001 20.657 24.8153 21.1966 24.4731 21.6413C24.1308 22.0861 23.6493 22.4124 23.1 22.5717Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    </div>

                    <p className={styles["stat-text"]} style={{color: "#FF9F7D"}}>
                        {validCheckIns}
                    </p>

                </div>
                <div className={styles["stat-container"]}>

                    <div className={styles["stat-header"]} style={{backgroundColor: "#FE614C"}}>
                        <div className={styles["stat-header-icon-container"]}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="26" viewBox="0 0 32 26" fill="none">
                                <path d="M18.3333 2.69121H22.3333C23.0406 2.69121 23.7189 2.96273 24.219 3.44605C24.719 3.92937 25 4.58489 25 5.26841V6.557M18.3333 23.3088H22.3333C23.0406 23.3088 23.7189 23.0373 24.219 22.5539C24.719 22.0706 25 21.4151 25 20.7316V19.443M9.00133 13H9M2.9 22.5717L10.9 24.8912C11.2982 25.0067 11.7189 25.0307 12.1284 24.9611C12.538 24.8915 12.925 24.7303 13.2585 24.4904C13.5921 24.2505 13.8631 23.9386 14.0497 23.5795C14.2363 23.2204 14.3334 22.8241 14.3333 22.4222V3.57776C14.3334 3.17592 14.2363 2.77962 14.0497 2.42051C13.8631 2.06141 13.5921 1.74946 13.2585 1.50958C12.925 1.26969 12.538 1.10852 12.1284 1.03893C11.7189 0.969348 11.2982 0.993277 10.9 1.10881L2.9 3.42829C2.35069 3.58764 1.86918 3.9139 1.52692 4.35866C1.18465 4.80341 0.999874 5.34296 1 5.89724V20.1028C0.999874 20.657 1.18465 21.1966 1.52692 21.6413C1.86918 22.0861 2.35069 22.4124 2.9 22.5717Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M23.9998 13H30.6665M30.6665 13L27.9998 10.4229M30.6665 13L27.9998 15.5772" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <p className={styles["stat-header-text"]}>Salidas</p>
                    </div>

                    <p className={styles["stat-text"]} style={{color: "#FE614C"}}>
                        {validCheckOuts}
                    </p>

                </div>
            </div>

            <div className={styles["graph-container"]}>
                <div style={{
                    width: '95%',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                }}>
                    <div style={{
                        fontFamily: 'Inter, sans-serif',
                        color: 'black',
                        fontSize: '16px',
                        fontWeight: 600,
                        marginBottom: '20px', // Adjust the margin as needed
                        marginTop: '40px',
                        marginLeft: '50px',
                        textAlign: "left"
                    }}>
                        Comportamiento de fichadas
                    </div>
                </div>

                <div style={{
                    width: '90%',
                    height: '90%'
                }}>
                    <BarChart
                        series={[
                            {data: entradasPorTiempo, color: '#FF9F7D', showMark: false, label: "Entradas"},
                            {data: salidasPorTiempo, color: '#FE614C', showMark: false, label: "Salidas"}
                        ]}
                        xAxis={[{ scaleType: 'band', data: xLabels }]}
                        hideLegend
                        sx={{
                            width: '100%',
                            height: '100%',
                            '& .MuiChartsAxisHighlight-root': {
                                stroke: "rgba(106, 199, 49, 0.25)",
                                strokeDasharray: "none"
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: "transparent",
                                strokeDasharray: "none"
                            },
                            '& .MuiChartsAxis-tick': {
                                stroke: "#F2F2F2",
                                strokeDasharray: "none"
                            },
                        }}
                        slots={{axisTickLabel: CustomAxisTickLabel}}
                        slotProps={{ axisLine: {style: {stroke: "transparent", strokeDasharray: "none"}}, axisTick: {style: {stroke: "#F2F2F2", strokeDasharray: "none"}} }}
                    />
                </div>
            </div>

        </div>



    </div>
    );
};


const ResumenControlBoardWrapper = ({org}) => {
    if (!org.flags.hasPresenteeismTable) {
        return <Navigate to="/" />;
      }
    
      return <ResumenControlBoard org={org} />;
}

export default ResumenControlBoardWrapper;
